import type { Disposition, ReasonCode, Stage } from '../api/types'

/**
 * Single source of truth for stage labels / variants / allowed
 * transitions. Mirrors `middleware/app/models/rma.py`'s frozensets 1:1
 * — keep the two in sync by hand (same discipline as sales-inbox's
 * stage-meta.ts).
 */

export const ALL_STAGES: Stage[] = [
  'requested',
  'approved',
  'denied',
  'rma_issued',
  'return_received',
  'disposition_set',
  'resolved',
  'cancelled',
]

export const TERMINAL_STAGES: ReadonlySet<Stage> = new Set([
  'denied',
  'resolved',
  'cancelled',
])

// Kanban column order — same 8 stages, board order rather than enum order.
export const BOARD_STAGES: Stage[] = [
  'requested',
  'approved',
  'rma_issued',
  'return_received',
  'disposition_set',
  'resolved',
  'denied',
  'cancelled',
]

export const STAGE_LABELS: Record<Stage, string> = {
  requested: 'Requested',
  approved: 'Approved',
  denied: 'Denied',
  rma_issued: 'RMA Issued',
  return_received: 'Return Received',
  disposition_set: 'Disposition Set',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
}

export const STAGE_SHORT_LABELS: Record<Stage, string> = {
  requested: 'Requested',
  approved: 'Approved',
  denied: 'Denied',
  rma_issued: 'Issued',
  return_received: 'Received',
  disposition_set: 'Disposition',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<
  Stage,
  'neutral' | 'success' | 'warning' | 'danger' | 'info'
> = {
  requested: 'neutral',
  approved: 'info',
  denied: 'danger',
  rma_issued: 'info',
  return_received: 'warning',
  disposition_set: 'warning',
  resolved: 'success',
  cancelled: 'danger',
}

/**
 * Permissive transition table — mirrors `ALLOWED_TRANSITIONS` in
 * `app/models/rma.py`: any non-terminal stage can move to any other
 * stage; terminals can only undo back to `requested`.
 */
export function transitionAllowed(from: Stage, to: Stage): boolean {
  if (from === to) return false
  if (TERMINAL_STAGES.has(from)) return to === 'requested'
  return true
}

export const REASON_LABELS: Record<ReasonCode, string> = {
  overship: 'Overship',
  wrong_item: 'Wrong item / quantity',
  customer_change_of_mind: 'Customer change of mind',
  quality_contamination: 'Quality — contamination',
  off_spec: 'Quality — off-spec',
  damaged_in_transit: 'Damaged in transit',
  expired_shelf_life: 'Expired shelf life',
  other: 'Other',
}

export const ALL_REASON_CODES: ReasonCode[] = [
  'overship',
  'wrong_item',
  'customer_change_of_mind',
  'quality_contamination',
  'off_spec',
  'damaged_in_transit',
  'expired_shelf_life',
  'other',
]

// Reason codes that imply a quality/lot investigation — the UI nudges
// toward `qc_hold` disposition for these, but does not hard-block other
// choices (mirrors QUALITY_REASON_CODES in app/models/rma.py).
export const QUALITY_REASON_CODES: ReadonlySet<ReasonCode> = new Set([
  'quality_contamination',
  'off_spec',
])

export const DISPOSITION_LABELS: Record<Disposition, string> = {
  pending: 'Pending',
  restock: 'Restock',
  qc_hold: 'QC Hold',
  scrap: 'Scrap',
  credit_only: 'Credit only',
}

export const ALL_DISPOSITIONS: Disposition[] = [
  'pending',
  'restock',
  'qc_hold',
  'scrap',
  'credit_only',
]
